'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/calc-extra'

const RiemannSums = dynamic(() => import('../_components/widgets/CalcExtraWidgets').then(m => m.RiemannSums), { ssr: false })
const Accumulator = dynamic(() => import('../_components/widgets/CalcExtraWidgets').then(m => m.Accumulator), { ssr: false })

export default function Integrals() {
  return (
    <Lesson>
      <Step>
        <H>The other half of calculus</H>
        <P>
          Derivatives cut things into tiny pieces to find a rate of change. <strong>Integrals</strong> go the other way: they add
          up tiny pieces to get a total. The classic question: what’s the <strong>area</strong> under a curve?
        </P>
        <P>
          There’s no formula for the area of a curvy shape, but there is for rectangles. So fill the shape with thin rectangles
          and add them up. The thinner they are, the better the fit.
        </P>
        <Figure n="4.12" wide caption="Drag n up. The rectangles hug the curve more and more closely, and their total area closes in on the true area.">
          <RiemannSums />
        </Figure>
      </Step>

      <Step gate={['q-notation', 'nq-area']}>
        <H>Reading the notation</H>
        <MB>{'\\int_a^b f(x)\\,dx \\;\\approx\\; \\sum_{i} f(x_i)\\,\\Delta x'}</MB>
        <P>
          The tall S, <M>{'\\int'}</M>, is a stretched “S” for <em>sum</em>. <M>a</M> and <M>b</M> are where you start and stop.{' '}
          <M>{'f(x)'}</M> is each rectangle’s height, and <M>{'dx'}</M> means its width, shrunk towards zero. It’s the same Σ
          loop from Module 2, with infinitely thin slices.
        </P>
        <Quiz
          id="q-notation"
          question="What happens to the area when the curve dips below the x-axis?"
          options={[
            { label: 'That part counts as negative area', correct: true },
            { label: 'It’s ignored', why: 'The rectangles there have negative height, so they subtract.' },
            { label: 'It counts as positive area', why: 'f(x) is negative there, and f(x)·Δx is negative too.' },
          ]}
        />
        <NumberQuiz
          id="nq-area"
          question={
            <>
              What is <M>{'\\int_0^3 4\\,dx'}</M>? (The “curve” is a flat line at height 4.)
            </>
          }
          answer={12}
          explain="A rectangle 3 wide and 4 tall: area 12."
          hint="Picture the shape: it’s a plain rectangle."
        />
      </Step>

      <Step gate="riemann">
        <H>Compute areas</H>
        <Exercise
          id="riemann"
          title="area and average"
          {...EX.riemann}
          hints={[
            <>
              <C>{'const w = (b - a) / n'}</C>, then add <C>{'f(a + (i + 0.5) * w) * w'}</C> for each <C>i</C>.
            </>,
            <>
              <C>{'return area(f, a, b) / (b - a)'}</C>
            </>,
          ]}
        />
        <Callout kind="why">
          That last line is a sneak preview of Module 5. In probability, the area under a curve between two points is the{' '}
          <em>chance</em> of landing between them. The whole area under a probability curve is always exactly 1.
        </Callout>
      </Step>

      <Step gate="q-ftc">
        <H>Areas and slopes are opposites</H>
        <P>
          Imagine water flowing into a tank at a rate <M>{'f(t)'}</M>. The total in the tank so far is the area under the rate
          curve, <M>{'A(x)'}</M>. Now ask: how fast is the total growing right now? Exactly at the current rate,{' '}
          <M>{'f(x)'}</M>.
        </P>
        <MB>{'\\frac{d}{dx} \\int_0^x f(t)\\,dt = f(x)'}</MB>
        <Figure n="4.13" wide caption="Top: the rate. Bottom: the running total (the area so far). The slope of the bottom curve at x always equals the height of the top curve at x.">
          <Accumulator />
        </Figure>
        <Quiz
          id="q-ftc"
          question="Where is the total A(x) growing fastest?"
          options={[
            { label: 'Where f(x) is highest', correct: true },
            { label: 'Where A(x) is highest', why: 'A being big means a lot has collected so far — not that it’s collecting fast now.' },
            { label: 'Where f(x) is zero', why: 'Then nothing is flowing in, so the total stays flat.' },
          ]}
          explain="This is the Fundamental Theorem of Calculus: integrating and differentiating undo each other. It’s the reason the two halves of calculus fit together."
        />
      </Step>

      <Step gate="monte-carlo">
        <H>Areas by throwing darts</H>
        <P>
          Rectangles get slow when there are many dimensions. In 100 dimensions, even 10 slices per axis means{' '}
          <M>{'10^{100}'}</M> boxes. So for high-dimensional areas we <strong>sample</strong> instead: throw random points and
          count the fraction that land inside. This is the <strong>Monte Carlo</strong> method, named after the casino.
        </P>
        <Exercise
          id="monte-carlo"
          title="Estimate π with darts"
          {...EX.monteCarlo}
          hints={[
            <>
              Count the darts with <C>{'x * x + y * y <= 1'}</C>, then return <C>{'4 * inside / n'}</C>.
            </>,
          ]}
        />
        <Callout kind="note">
          Notice the error shrinks slowly: 100× more darts gets you about 10× more accuracy. But that’s true in 2 dimensions or
          2 000, which is why AI leans on sampling everywhere — from training on random batches to generating text one random
          token at a time.
        </Callout>
      </Step>
    </Lesson>
  )
}
